import {
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';

/**
 * Système d'interface minimal, bâti sur `@repere/ui-tokens`.
 *
 * Quatre éléments seulement : bouton, champ, carte, alerte. Chacun est un élément HTML
 * natif habillé, jamais une reconstitution — un `div` cliquable n'est ni focusable, ni
 * annoncé, ni actionnable au clavier, et l'application deviendrait inutilisable pour
 * une partie des parents.
 */

export function Bouton({
  children,
  variante = 'principal',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * React 19 passe `ref` comme une prop ordinaire, mais `ButtonHTMLAttributes` ne la
   * déclare pas. Elle est ici pour que le focus puisse être repris à la main — après le
   * retrait d'une ligne de formulaire, par exemple, dont le bouton emportait le focus.
   */
  ref?: Ref<HTMLButtonElement>;
  /**
   * `lien` habille un bouton en lien. Réservé aux actions qui **ne mènent nulle part** —
   * ouvrir un dialogue, dérouler une section : elles doivent rester des `<button>` pour
   * être annoncées comme telles et ne pas s'ouvrir dans un nouvel onglet, tout en gardant
   * la discrétion d'un lien quand elles sont secondaires.
   */
  variante?: 'principal' | 'secondaire' | 'lien';
}) {
  const modificateur = variante === 'principal' ? '' : ` ${variante}`;
  return (
    <button
      type="button"
      {...props}
      className={`bouton${modificateur}${props.className ? ` ${props.className}` : ''}`}
    >
      {children}
    </button>
  );
}

export function Champ({
  label,
  aide,
  erreur,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  /**
   * `ReactNode` et non `string` : un libellé peut avoir une part visible courte et une
   * part réservée aux lecteurs d'écran (voir `Invisible`). « Prénom » suffit à l'œil, qui
   * voit sous quel enfant le champ est rangé ; à l'oreille, trois « Prénom » de suite ne
   * se distinguent pas, et il faut « Prénom de l'enfant 2 ».
   */
  label: ReactNode;
  aide?: string;
  erreur?: string;
}) {
  const id = useId();
  const aideId = `${id}-aide`;
  const erreurId = `${id}-erreur`;
  // Le champ pointe vers son aide et son erreur : un lecteur d'écran les annonce
  // avec le champ, au lieu de les laisser orphelines à côté.
  const decrit = [aide ? aideId : null, erreur ? erreurId : null].filter(Boolean).join(' ');

  return (
    <div className="champ">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        {...props}
        aria-describedby={decrit || undefined}
        aria-invalid={erreur ? true : undefined}
      />
      {aide ? (
        <p className="aide" id={aideId}>
          {aide}
        </p>
      ) : null}
      {erreur ? (
        <p className="erreur" id={erreurId}>
          {erreur}
        </p>
      ) : null}
    </div>
  );
}

export function Carte({ titre, children }: { titre?: string; children: ReactNode }) {
  return (
    <section className="carte">
      {titre ? <h2 style={{ marginTop: 0 }}>{titre}</h2> : null}
      {children}
    </section>
  );
}

/**
 * Alerte d'interface. Toujours ambrée : la charte proscrit le rouge, dont l'agressivité
 * n'a pas sa place dans un produit conçu pour apaiser (docs/01 §3).
 */
export function Alerte({
  titre,
  children,
  urgent = false,
}: {
  titre?: string;
  children: ReactNode;
  urgent?: boolean;
}) {
  return (
    <div className="alerte" role={urgent ? 'alert' : 'status'} aria-live={urgent ? 'assertive' : 'polite'}>
      {titre ? <h3>{titre}</h3> : null}
      <p>{children}</p>
    </div>
  );
}

export function Invisible({ children }: { children: ReactNode }) {
  return <span className="invisible">{children}</span>;
}

export { ChampDate, ChampDateNaissance } from './ChampDateNaissance';
export { ChampJours } from './ChampJours';
export { ChampMotDePasse } from './ChampMotDePasse';
export { Galets } from './Galets';
export { Logo } from './Logo';
export { Picto } from './Picto';
export { Vide } from './Vide';
export { ChoixTheme, appliquerTheme, themeConserve, type Theme } from './Theme';

export { useRequeteMedia, ECRAN_LARGE } from './media';
